import { Box } from '@mui/material';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Spinner from '/@/components/spinner';
import { useFieldById } from '/@/hooks/fields';
import { useRecommendationById } from '/@/hooks/my-recommendations/useFilterMyRecommendations';
import { useGetUserNode } from '/@/hooks/nodes';
import { useProducts } from '/@/hooks/recommendation';
import { updateRecommendationFromAPI } from '/@/store/private-data/recommendations/apiProviders';
import { getCropConfig } from '/@/store/private-data/recommendations/selectors';
import { getBrands } from '/@/store/public-data/selectors';
import { getAppInstance, getSessionBrands, getSessionUnits, getUser } from '/@/store/session/selectors';
import { useRecommendationResultData } from '../recommendation-aio/hooks';
import RecommendationPDF from '../recommendation-pdf';

const RecommendationOnlyReadWithBackup = ({ crop, recommendationId, language }) => {
    const { data: recommendation } = useRecommendationById(recommendationId);

    const user = useSelector(getUser);
    const appInstance = useSelector(getAppInstance);
    const units = useSelector(getSessionUnits);
    appInstance.brands = useSelector(getBrands);

    const parsedRecommendation = useMemo(
        () => (recommendation ? { ...recommendation, ...recommendation.backup } : undefined),
        [recommendation]
    );

    const userNodeQuery = useGetUserNode(recommendation?.userNode);

    if (!parsedRecommendation || userNodeQuery.isLoading)
        return (
            <Box alignItems={'center'} justifyContent={'center'} width={'100%'} height={'100%'} display={'flex'}>
                <Spinner />
            </Box>
        );

    return (
        <RecommendationPDF
            user={user}
            appInstance={appInstance}
            language={language}
            units={units}
            recommendation={parsedRecommendation}
            userNode={userNodeQuery.data}
        />
    );
};

const RecommendationOnlyReadWithouthBackup = ({ crop, recommendationId, language }) => {
    const { data: recommendation } = useRecommendationById(recommendationId);
    const recommendationWithProducts = useMemo(() => {
        if (!recommendation) return;
        return {
            ...recommendation,
            crops: [
                {
                    ...recommendation.crops?.[0],
                    products: recommendation.crops?.[0][`${recommendation.crops?.[0].crop}Products`],
                },
            ],
        };
    }, [recommendation]);

    const debouncedUpdateRecommendation = useCallback(debounce(updateRecommendationFromAPI, 1000), []);

    const user = useSelector(getUser);
    const appInstance = useSelector(getAppInstance);
    const units = useSelector(getSessionUnits);
    appInstance.brands = useSelector(getBrands);

    const fieldQuery = useFieldById({ id: recommendation?.field }, { enabled: !!recommendation?.field });
    const sessionBrands = useSelector(getSessionBrands);
    const cropConfig = getCropConfig(recommendationWithProducts, crop);

    const brand = useMemo(() => {
        console.log('brand = useMemo: cropConfig', cropConfig);
        console.log('brand = useMemo: sessionBrands', sessionBrands);
        return cropConfig ? sessionBrands[cropConfig.crop][0] : {};
    }, [cropConfig, sessionBrands]); // TODO: BRAND

    const productsQuery = useProducts({
        lat: fieldQuery.data?.lat,
        lng: fieldQuery.data?.lng,
        crop: cropConfig?.crop,
        plantingDate: cropConfig?.plantingDate,
        brandCodename: sessionBrands[cropConfig?.crop][0]?.codename,
        environment: cropConfig?.environment,
    });

    const mappedProducts = useMemo(() => {
        return [...(productsQuery.data?.featuredProducts || []), ...(productsQuery.data?.otherProducts || [])].reduce(
            (pV, cV) => {
                pV[cV.id] = cV;
                return pV;
            },
            {}
        );
    }, [productsQuery.data?.featuredProducts, productsQuery.data?.otherProducts]);

    const {
        metricsQuery,
        featuresQuery,
        soyPlantingRecommendationQuery,
        cornDensityCurveQuery,
        fertilizationCoefficientsQuery,
        pestControlPlans,
        catalogBrands,
        productsWithAPIData,
    } = useRecommendationResultData({
        field: fieldQuery.data,
        crop,
        cropConfig,
        mappedProducts,
    });

    const parsedRecommendation = useMemo(() => {
        if (!recommendationWithProducts) return undefined;
        return {
            ...recommendationWithProducts,
            brand,
            field: fieldQuery.data,
            crops: [
                {
                    ...recommendationWithProducts.crops?.[0],
                    productRecommendations: productsWithAPIData,
                    pestControlPlans: pestControlPlans,
                    catalogBrands: catalogBrands,
                },
            ],
        };
    }, [brand, catalogBrands, fieldQuery.data, pestControlPlans, productsWithAPIData, recommendationWithProducts]);

    useEffect(() => {
        if (
            !parsedRecommendation ||
            metricsQuery.isLoading ||
            featuresQuery.isLoading ||
            soyPlantingRecommendationQuery.isLoading ||
            cornDensityCurveQuery.isLoading ||
            fertilizationCoefficientsQuery.isLoading ||
            productsQuery.isLoading ||
            recommendation.backup ||
            !fieldQuery.data
        )
            return;

        debouncedUpdateRecommendation({
            ...recommendation,
            crops: [
                {
                    ...recommendation.crops[0],
                    products: recommendation?.crops?.[0]?.[`${crop}Products`],
                },
            ],
            status: 'read_only',
            backup: {
                ...recommendation,
                status: 'read_only',
                crops: [
                    {
                        ...recommendation?.crops?.[0],
                        products: recommendation?.crops?.[0]?.[`${crop}Products`],
                        productRecommendations: productsWithAPIData,
                        pestControlPlans,
                        catalogBrands,
                    },
                ],
                weeds: [],
                field: fieldQuery.data,
                brand,
            },
        });
    }, [
        brand,
        catalogBrands,
        cornDensityCurveQuery.isLoading,
        crop,
        debouncedUpdateRecommendation,
        featuresQuery.isLoading,
        fertilizationCoefficientsQuery.isLoading,
        fieldQuery.data,
        metricsQuery.isLoading,
        parsedRecommendation,
        pestControlPlans,
        productsQuery.isLoading,
        productsWithAPIData,
        recommendation,
        soyPlantingRecommendationQuery.isLoading,
    ]);

    const userNodeQuery = useGetUserNode(recommendation?.userNode);

    if (
        !parsedRecommendation ||
        metricsQuery.isLoading ||
        featuresQuery.isLoading ||
        soyPlantingRecommendationQuery.isLoading ||
        cornDensityCurveQuery.isLoading ||
        fertilizationCoefficientsQuery.isLoading ||
        productsQuery.isLoading ||
        fieldQuery.isLoading ||
        userNodeQuery.isLoading
    )
        return (
            <Box alignItems={'center'} justifyContent={'center'} width={'100%'} height={'100%'} display={'flex'}>
                <Spinner />
            </Box>
        );

    return (
        <RecommendationPDF
            user={user}
            appInstance={appInstance}
            language={language}
            units={units}
            recommendation={parsedRecommendation}
            userNode={userNodeQuery.data}
        />
    );
};

const RecommendationOnlyReadWrapper = ({ crop, recommendationId, language }) => {
    const { data: recommendation, isLoading } = useRecommendationById(recommendationId);

    if (isLoading) {
        return (
            <Box alignItems={'center'} justifyContent={'center'} width={'100%'} height={'100%'} display={'flex'}>
                <Spinner />
            </Box>
        );
    }

    if (Object.keys(recommendation?.backup || {}).length > 0) {
        return <RecommendationOnlyReadWithBackup crop={crop} recommendationId={recommendationId} language={language} />;
    }
    return <RecommendationOnlyReadWithouthBackup crop={crop} recommendationId={recommendationId} language={language} />;
};

export default RecommendationOnlyReadWrapper;
